import { Grid, InputAdornment } from "@mui/material";
import { useAppSelector } from "app/hooks";
import { OLClearIcon } from "components/CustomIcons/OLClearIcon";
import { MultiWithModal } from "components/FormControls/MultiWithModal";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { useMessage } from "hooks/intl";
import { memo } from "react";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { useStyles } from "../Transaction.styles";

interface Props {
  index: number;
  remove: (index: number) => void;
}

export const Product = memo(({ index, remove }: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { currency } = useAppSelector(selectCurrentStoreData);

  return (
    <div className={classes.productRow}>
      <div className={classes.itemHeader}>
        {message({ id: "product" })} {index + 1}
      </div>
      {index > 0 && (
        <OLClearIcon
          className={classes.clearicon}
          onClick={() => remove(index)}
        />
      )}
      <Grid container spacing={10}>
        <Grid xs={12} md={4} lg={3} item>
          <TextInput
            className={classes.fullWidth}
            label={<RequiredFieldLabel text={message({ id: "productName" })} />}
            name={`items[${index}].name`}
            renderAsFastField
            data-test-id="product-name-input"
          />
        </Grid>
        <Grid xs={12} md={4} lg={3} item>
          <TextInput
            className={classes.fullWidth}
            label={<RequiredFieldLabel text={message({ id: "productSKU" })} />}
            name={`items[${index}].sku`}
            renderAsFastField
            data-test-id="product-sku-input"
          />
        </Grid>
        <Grid xs={12} md={4} lg={3} item>
          <NumberFormatInput
            allowDecimals
            decimalScale={3}
            data-test-id="quantity-input"
            label={
              <RequiredFieldLabel
                text={message({
                  id: "quantity",
                })}
              />
            }
            name={`items[${index}].highPrecisionQuantity`}
          />
        </Grid>
        <Grid xs={12} md={4} lg={3} item>
          <NumberFormatInput
            allowDecimals
            data-test-id="gross-value-input"
            label={
              <RequiredFieldLabel
                text={message({
                  id: "grossValue",
                })}
              />
            }
            name={`items[${index}].grossValue`}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">{currency}</InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid xs={12} md={4} lg={3} item>
          <TextInput
            className={classes.fullWidth}
            label={message({ id: "brandName" })}
            name={`items[${index}].maker`}
            renderAsFastField
            data-test-id="brand-name-input"
          />
        </Grid>
        <Grid xs={12} md={4} lg={3} item>
          <TextInput
            className={classes.fullWidth}
            label={<RequiredFieldLabel text={message({ id: "category" })} />}
            name={`items[${index}].category`}
            renderAsFastField
            data-test-id="category-input"
          />
        </Grid>
        <Grid xs={12} md={4} lg={3} item>
          <MultiWithModal
            className={classes.fullWidth}
            formField={`items[${index}].labels`}
            label={message({ id: "customAttributes" })}
            placeholder={message({
              id: "addCustomAttribute",
            })}
            data-test-id="products-custom-attributes"
          />
        </Grid>
      </Grid>
    </div>
  );
});
